/**
 * ComposeEmailDrawer
 *
 * Slide-in compose panel that replaces "navigate to /email-hub" for the
 * per-org / per-contact "Send Email" flow.
 *
 * Why Radix Dialog primitives?
 * ----------------------------
 * The Data Preview screen renders OrgQuickView INSIDE a Radix Dialog. That
 * parent Dialog installs a FocusScope that traps focus inside its own
 * content. If we render the drawer as a plain `<div>` (even at a higher
 * z-index), it sits visually above the parent dialog but the parent's
 * FocusScope still owns focus — so inputs in our drawer can't be focused
 * or typed into, and clicks are intercepted by the parent's pointer trap.
 *
 * Wrapping the drawer in `<DialogPrimitive.Root modal>` makes Radix push
 * a NEW FocusScope onto its stack. The most-recently-opened dialog
 * always wins focus, which is exactly the behaviour we want here.
 *
 * Behaviour
 * ----------
 * 1. Hidden completely when `open === false`. No visual footprint.
 * 2. When `open && !minimized` → slides in from the right; takes ~520px of
 *    screen real-estate. Sits ABOVE the page and above any parent Radix
 *    dialog (via z-[60] + nested FocusScope).
 * 3. When `open && minimized` → collapses to a thin vertical tab on the
 *    right edge of the viewport, vertically centered, that reads "Email"
 *    with a badge if attachments are present.
 * 4. Send pipes through `POST /api/send-email` — the same endpoint
 *    EmailHubPage uses — so the backend continues to record every send
 *    in `sent_emails` AND drop a row in `log_org_change` (i.e. the
 *    organisation's notes timeline) automatically.
 * 5. Closing with a dirty draft prompts a confirm dialog (handled in
 *    context). After a successful send the drawer auto-closes.
 *
 * Attachment behaviour mirrors EmailHubPage: upload via `POST
 * /api/attachments`, render with `<AttachmentList />`.
 */
import React, { useRef, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useComposeEmail } from '../context/ComposeEmailContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { AttachmentList } from './AttachmentPreview';
import { authJsonFetch } from '../lib/api';
import { toast } from 'sonner';
import {
  Send,
  Mail,
  Paperclip,
  X,
  Minus,
  Loader2,
  ChevronDown,
  ChevronUp,
  Bell,
  Sparkles,
  CheckCircle,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

const MinimisedTab = () => {
  const { restore, draft } = useComposeEmail();
  const attachmentCount = draft?.attachments?.length || 0;
  return (
    <button
      type="button"
      onClick={restore}
      data-testid="compose-email-minimised-tab"
      title="Open compose email"
      className="fixed right-0 top-1/2 -translate-y-1/2 z-[60] group"
      style={{ writingMode: 'vertical-rl' }}
    >
      <span
        className="flex items-center gap-2 px-2 py-4 bg-primary text-primary-foreground rounded-l-md shadow-lg
                   hover:px-3 transition-all border border-r-0 border-primary/40"
      >
        <Mail className="w-4 h-4 rotate-90" />
        <span className="font-medium text-sm tracking-wide">Email</span>
        {attachmentCount > 0 && (
          <Badge
            variant="secondary"
            className="rotate-90 px-1.5 py-0 text-[10px] leading-none"
          >
            {attachmentCount}
          </Badge>
        )}
      </span>
    </button>
  );
};

const handleFileUpload = async (e, setDraft) => {
  const files = Array.from(e.target.files || []);
  if (files.length === 0) return;
  for (const file of files) {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const token = localStorage.getItem('gs_session_token');
      const response = await fetch(`${API}/attachments`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      if (response.ok) {
        const attachment = await response.json();
        setDraft((prev) => ({
          ...prev,
          attachments: [...(prev.attachments || []), attachment],
        }));
        toast.success(`${file.name} attached`);
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || `Failed to upload ${file.name}`);
      }
    } catch (err) {
      toast.error(`Upload error: ${file.name}`);
    }
  }
  e.target.value = '';
};

export const ComposeEmailDrawer = () => {
  const {
    open,
    minimized,
    draft,
    setDraft,
    closeCompose,
    minimize,
    reset,
    pendingAction,
    confirmPending,
    cancelPending,
  } = useComposeEmail();

  const [sending, setSending] = useState(false);
  const [ccBccOpen, setCcBccOpen] = useState(false);
  const [showNudge, setShowNudge] = useState(false);
  // AI Polish — mirrors the EmailHubPage flow (compose pop-up): tone
  // selector + Polish button → side-by-side "review before applying"
  // dialog. Uses the same `/api/ai/polish-email` endpoint which now
  // honours the operator's AI Provider configuration in Settings.
  const [polishing, setPolishing] = useState(false);
  const [polishTone, setPolishTone] = useState('professional');
  const [polishedText, setPolishedText] = useState('');
  const [originalBody, setOriginalBody] = useState('');
  const [showPolishConfirm, setShowPolishConfirm] = useState(false);
  const fileInputRef = useRef(null);

  const handlePolish = async () => {
    if (!draft.body?.trim()) {
      toast.error('Write a message first, then polish it');
      return;
    }
    setPolishing(true);
    setOriginalBody(draft.body);
    try {
      const response = await authJsonFetch(`${API}/ai/polish-email`, {
        method: 'POST',
        body: JSON.stringify({ text: draft.body, tone: polishTone }),
      });
      if (response.ok) {
        const data = await response.json();
        setPolishedText(data.polished_text || '');
        setShowPolishConfirm(true);
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'AI polish failed');
      }
    } catch (err) {
      toast.error('AI service error');
    } finally {
      setPolishing(false);
    }
  };

  const acceptPolish = () => {
    setDraft({ body: polishedText, ai_polished: true });
    setShowPolishConfirm(false);
    toast.success('Polished text applied');
  };

  const rejectPolish = () => {
    setShowPolishConfirm(false);
    toast.info('Kept original text');
  };

  // Don't render anything when fully closed (other than the confirm dialog
  // which we still want available for late-firing actions).
  if (!open) {
    return (
      <DiscardConfirmDialog
        pendingAction={pendingAction}
        onConfirm={confirmPending}
        onCancel={cancelPending}
      />
    );
  }

  if (minimized) {
    return (
      <>
        <MinimisedTab />
        <DiscardConfirmDialog
          pendingAction={pendingAction}
          onConfirm={confirmPending}
          onCancel={cancelPending}
        />
      </>
    );
  }

  const removeAttachment = (id) => {
    setDraft((prev) => ({
      ...prev,
      attachments: (prev.attachments || []).filter((a) => a.attachment_id !== id),
    }));
  };

  const handleSend = async () => {
    if (!draft.to_email || !draft.to_email.includes('@')) {
      toast.error('Recipient email is required');
      return;
    }
    if (!draft.subject?.trim()) {
      toast.error('Subject is required');
      return;
    }
    setSending(true);
    try {
      const payload = {
        ...draft,
        nudge_2_hours: (draft.nudge_2_days || 0) * 24,
        nudge_3_hours: (draft.nudge_3_weeks || 0) * 24 * 7,
      };
      const response = await authJsonFetch(`${API}/send-email`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        const result = await response.json().catch(() => ({}));
        if (result.status === 'logged') {
          toast.warning(result.message || 'Email logged only — connect Gmail to actually send');
        } else {
          toast.success('Email sent successfully');
        }
        reset();
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to send email');
      }
    } catch (err) {
      toast.error('An error occurred while sending');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/*
        Wrap the slide-in panel in Radix's Dialog primitive so it stacks
        ABOVE any other Radix dialog that may already be open (e.g. the
        Data Preview screen). Radix's FocusScope stack means the most
        recently opened dialog owns focus + clicks, which is what makes
        inputs inside our drawer typeable when called from Data Preview.

        - `modal={true}`: take focus, install a pointer-event-blocking
          overlay sibling.
        - `onPointerDownOutside` & `onEscapeKeyDown`: re-route to our
          minimise/close handlers so dirty drafts are never silently
          destroyed by a stray click or Escape press.
        - Custom Overlay + Content (NOT the shadcn wrapper) so we control
          positioning (right-anchored slide-in, not centered modal).
      */}
      <DialogPrimitive.Root
        open
        onOpenChange={(v) => {
          // Radix calls onOpenChange(false) on Escape / outside-click. We
          // never want either to silently destroy a draft, so route them
          // through minimise.
          if (!v) minimize();
        }}
      >
        <DialogPrimitive.Portal>
          {/* Backdrop — click to minimise rather than close, so dirty
              drafts aren't lost to a stray click. */}
          <DialogPrimitive.Overlay
            className="fixed inset-0 z-[55] bg-black/20 backdrop-blur-[1px]
                       data-[state=open]:animate-in data-[state=closed]:animate-out
                       data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
            data-testid="compose-email-backdrop"
            onClick={minimize}
          />

          {/* Drawer panel */}
          <DialogPrimitive.Content
            aria-label="Compose Email"
            data-testid="compose-email-drawer"
            onPointerDownOutside={(e) => {
              // Don't let Radix close-on-outside-click destroy the draft.
              e.preventDefault();
              minimize();
            }}
            onInteractOutside={(e) => e.preventDefault()}
            onEscapeKeyDown={(e) => {
              e.preventDefault();
              minimize();
            }}
            className="fixed top-0 right-0 h-screen w-full sm:w-[520px] z-[60] bg-background
                       border-l border-border shadow-2xl flex flex-col outline-none
                       data-[state=open]:animate-in data-[state=closed]:animate-out
                       data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right
                       duration-200"
          >
            {/* Required for a11y by Radix even when visually hidden. */}
            <DialogPrimitive.Title className="sr-only">
              {draft.org_name ? `Email · ${draft.org_name}` : 'New email'}
            </DialogPrimitive.Title>
            <DialogPrimitive.Description className="sr-only">
              Compose and send an email to the selected organisation.
            </DialogPrimitive.Description>

            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
              <div className="flex items-center gap-2 min-w-0">
                <Mail className="w-4 h-4 text-primary shrink-0" />
                <h3 className="font-semibold text-sm truncate" title={draft.org_name || 'New email'}>
                  {draft.org_name ? `Email · ${draft.org_name}` : 'New email'}
                </h3>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={minimize}
                  title="Minimise (keeps draft)"
                  data-testid="compose-email-minimise-btn"
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => closeCompose(false)}
                  title="Close"
                  data-testid="compose-email-close-btn"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Form body + footer extracted to a sub-component for clarity. */}
            <ComposeBody
              draft={draft}
              setDraft={setDraft}
              ccBccOpen={ccBccOpen}
              setCcBccOpen={setCcBccOpen}
              showNudge={showNudge}
              setShowNudge={setShowNudge}
              fileInputRef={fileInputRef}
              removeAttachment={removeAttachment}
              sending={sending}
              handleSend={handleSend}
              polishing={polishing}
              polishTone={polishTone}
              setPolishTone={setPolishTone}
              handlePolish={handlePolish}
            />
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>

      <PolishReviewDialog
        open={showPolishConfirm}
        original={originalBody}
        polished={polishedText}
        onAccept={acceptPolish}
        onReject={rejectPolish}
      />

      <DiscardConfirmDialog
        pendingAction={pendingAction}
        onConfirm={confirmPending}
        onCancel={cancelPending}
      />
    </>
  );
};

/**
 * Form body + footer of the drawer. Extracted so the main component stays
 * readable and the JSX tree for the Radix Dialog wrapper isn't dwarfed by
 * 200 lines of inputs.
 */
const ComposeBody = ({
  draft,
  setDraft,
  ccBccOpen,
  setCcBccOpen,
  showNudge,
  setShowNudge,
  fileInputRef,
  removeAttachment,
  sending,
  handleSend,
  polishing,
  polishTone,
  setPolishTone,
  handlePolish,
}) => (
  <>
    <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
      {/* To */}
      <div>
        <Label className="text-xs font-medium">To</Label>
        <Input
          value={draft.to_email}
          onChange={(e) => setDraft({ to_email: e.target.value })}
          placeholder="recipient@example.com"
          data-testid="compose-email-to-input"
          className="h-9"
        />
        {draft.to_name && (
          <p className="text-[11px] text-muted-foreground mt-1">{draft.to_name}</p>
        )}
      </div>

      {/* CC / BCC toggle */}
      <button
        type="button"
        onClick={() => setCcBccOpen((v) => !v)}
        className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
        data-testid="compose-email-toggle-cc-bcc"
      >
        {ccBccOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        CC / BCC
      </button>

      {ccBccOpen && (
        <div className="space-y-2 pl-2 border-l-2 border-muted">
          <div>
            <Label className="text-xs">CC</Label>
            <Input
              value={draft.cc}
              onChange={(e) => setDraft({ cc: e.target.value })}
              placeholder="comma-separated"
              data-testid="compose-email-cc-input"
              className="h-9"
            />
          </div>
          <div>
            <Label className="text-xs">BCC</Label>
            <Input
              value={draft.bcc}
              onChange={(e) => setDraft({ bcc: e.target.value })}
              placeholder="comma-separated"
              data-testid="compose-email-bcc-input"
              className="h-9"
            />
          </div>
        </div>
      )}

      {/* Subject */}
      <div>
        <Label className="text-xs font-medium">Subject</Label>
        <Input
          value={draft.subject}
          onChange={(e) => setDraft({ subject: e.target.value })}
          placeholder="Subject"
          data-testid="compose-email-subject-input"
          className="h-9"
        />
      </div>

      {/* Body */}
      <div>
        <Label className="text-xs font-medium">Message</Label>
        <Textarea
          value={draft.body}
          onChange={(e) => setDraft({ body: e.target.value })}
          placeholder="Write your message…"
          data-testid="compose-email-body-input"
          className="min-h-[180px] resize-y"
        />
        {/* AI Polish — tone picker + button live just below the textarea
            so the writer's flow is: write → choose tone → click polish →
            review side-by-side → accept/reject. Mirrors EmailHubPage's
            compose. Disabled until there's something to polish. */}
        <div className="flex items-center gap-2 mt-2">
          <Select value={polishTone} onValueChange={setPolishTone}>
            <SelectTrigger
              className="h-8 w-[140px] text-xs"
              data-testid="compose-email-polish-tone"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="professional">Professional</SelectItem>
              <SelectItem value="friendly">Friendly</SelectItem>
              <SelectItem value="formal">Formal</SelectItem>
              <SelectItem value="casual">Casual</SelectItem>
            </SelectContent>
          </Select>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 px-2 text-xs"
            onClick={handlePolish}
            disabled={polishing || !draft.body?.trim()}
            data-testid="compose-email-polish-btn"
          >
            {polishing ? (
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            ) : (
              <Sparkles className="w-3 h-3 mr-1" />
            )}
            AI Polish
          </Button>
          {draft.ai_polished && (
            <Badge variant="secondary" className="text-[10px]" data-testid="compose-email-polished-badge">
              Polished
            </Badge>
          )}
        </div>
      </div>

      {/* Attachments */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <Label className="text-xs font-medium flex items-center gap-2">
            Attachments
            {(draft.attachments?.length || 0) > 0 && (
              <Badge variant="secondary" className="text-[10px]">
                {draft.attachments.length}
              </Badge>
            )}
          </Label>
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => fileInputRef.current?.click()}
            data-testid="compose-email-attach-btn"
          >
            <Paperclip className="w-3 h-3 mr-1" />
            Add
          </Button>
        </div>
        <AttachmentList
          attachments={draft.attachments || []}
          onRemove={removeAttachment}
          showPreview={true}
          emptyMessage="No attachments yet"
        />
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => handleFileUpload(e, setDraft)}
        />
      </div>

      {/* Nudge */}
      <div className="border-t pt-3">
        <button
          type="button"
          onClick={() => setShowNudge((v) => !v)}
          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
          data-testid="compose-email-toggle-nudge"
        >
          {showNudge ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          <Bell className="w-3 h-3" />
          Nudge / follow-up reminders
          {draft.nudge_enabled && <Badge variant="secondary" className="text-[10px] ml-1">On</Badge>}
        </button>
        {showNudge && (
          <div className="mt-2 pl-2 border-l-2 border-muted space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Enable auto follow-up</Label>
              <Switch
                checked={draft.nudge_enabled}
                onCheckedChange={(v) => setDraft({ nudge_enabled: v })}
                data-testid="compose-email-nudge-enabled"
              />
            </div>
            {draft.nudge_enabled && (
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-[10px]">1st (hrs)</Label>
                  <Input
                    type="number" min="0"
                    value={draft.nudge_1_hours}
                    onChange={(e) => setDraft({ nudge_1_hours: parseInt(e.target.value, 10) || 0 })}
                    className="h-8 text-xs"
                    data-testid="compose-email-nudge-1"
                  />
                </div>
                <div>
                  <Label className="text-[10px]">2nd (days)</Label>
                  <Input
                    type="number" min="0"
                    value={draft.nudge_2_days}
                    onChange={(e) => setDraft({ nudge_2_days: parseInt(e.target.value, 10) || 0 })}
                    className="h-8 text-xs"
                    data-testid="compose-email-nudge-2"
                  />
                </div>
                <div>
                  <Label className="text-[10px]">3rd (weeks)</Label>
                  <Input
                    type="number" min="0"
                    value={draft.nudge_3_weeks}
                    onChange={(e) => setDraft({ nudge_3_weeks: parseInt(e.target.value, 10) || 0 })}
                    className="h-8 text-xs"
                    data-testid="compose-email-nudge-3"
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>

    {/* Footer */}
    <div className="border-t px-4 py-3 flex items-center justify-between bg-muted/30">
      <p className="text-[11px] text-muted-foreground">
        Sent emails are recorded in Email Audit and the organisation&apos;s notes.
      </p>
      <Button
        onClick={handleSend}
        disabled={sending}
        data-testid="compose-email-send-btn"
      >
        {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
        Send
      </Button>
    </div>
  </>
);

const PolishReviewDialog = ({ open, original, polished, onAccept, onReject }) => {
  // Use shadcn Dialog (not Radix primitives directly) so we get the
  // standard centered modal styling. Sits ABOVE the slide-in drawer
  // because shadcn's overlay is z-50 + this Dialog is rendered after
  // the drawer is already mounted, so its FocusScope wins.
  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onReject(); }}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto z-[90]"
        data-testid="compose-email-polish-review-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            AI Polished Result — Review Before Applying
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
          <div>
            <Label className="text-muted-foreground">Original</Label>
            <div
              className="mt-2 p-4 bg-muted rounded-lg whitespace-pre-wrap text-sm max-h-80 overflow-y-auto"
              data-testid="compose-email-polish-original"
            >
              {original}
            </div>
          </div>
          <div>
            <Label className="text-primary">AI Polished</Label>
            <div
              className="mt-2 p-4 bg-primary/5 border border-primary/20 rounded-lg whitespace-pre-wrap text-sm max-h-80 overflow-y-auto"
              data-testid="compose-email-polish-result"
            >
              {polished}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onReject}
            data-testid="compose-email-polish-reject"
          >
            Keep Original
          </Button>
          <Button
            onClick={onAccept}
            data-testid="compose-email-polish-accept"
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            Apply Polished Text
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


const DiscardConfirmDialog = ({ pendingAction, onConfirm, onCancel }) => {
  const open = !!pendingAction;
  const isSwitchOrg = pendingAction?.type === 'switchOrg';
  const isReplace = pendingAction?.type === 'replace';

  const title = isSwitchOrg
    ? 'Email not yet sent'
    : isReplace
      ? 'Discard current draft?'
      : 'Discard email draft?';

  const description = isSwitchOrg
    ? "You're switching organisations while a half-written email is still open. What would you like to do with the draft?"
    : isReplace
      ? 'Opening a new compose for a different organisation will discard the current draft.'
      : 'You have unsaved changes in the email compose. Closing will discard them.';

  // For org-switch we offer two clear paths: "Finish email" (default,
  // non-destructive) and "Destroy email" (destructive). For other actions
  // we keep the standard "Keep editing" / "Discard" pair.
  const cancelLabel = isSwitchOrg ? 'Finish email' : 'Keep editing';
  const confirmLabel = isSwitchOrg ? 'Destroy email' : 'Discard';

  if (!open) return null;

  // We render this modal manually (not via Radix's AlertDialog) so we can
  // stack it ABOVE the slide-in drawer (z-[60]). Radix's default overlay
  // is z-50 which would have it disappear behind the drawer.
  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      data-testid="compose-email-discard-dialog"
      role="alertdialog"
      aria-modal="true"
    >
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onCancel}
      />
      <div className="relative bg-background border rounded-lg shadow-2xl w-full max-w-md p-6 space-y-4">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold">{title}</h2>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
          <Button
            variant="outline"
            onClick={onCancel}
            data-testid="compose-email-discard-cancel"
          >
            {cancelLabel}
          </Button>
          <Button
            onClick={onConfirm}
            variant="destructive"
            data-testid="compose-email-discard-confirm"
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ComposeEmailDrawer;
